import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import type { DualPathRequiredMutationResult } from '../dualPathMutationResult';

export interface InsertQuestionnaireInvitesInput {
  ThirdPartyId: string;
  UserEmails: string[];
  Message?: string;
  QuestionnaireTemplateVersionIds: string[];
}

/**
 * Maps the tRPC mutation response to the GraphQL mutation response shape so
 * consumers branching on the result do not need to know which path executed.
 *
 * `affected_rows` is the number of template VERSIONS invited on, not the number
 * of invite rows written — V2's contract, preserved end to end.
 */
const mapTrpcResponseToGraphQL = (data: { affected_rows: number }) => ({
  insertQuestionnaireInvites: {
    __typename: 'GenericMutationOutput' as const,
    affected_rows: data.affected_rows,
  },
});

export type InsertQuestionnaireInvitesResult = ReturnType<
  typeof mapTrpcResponseToGraphQL
>;
export type InsertQuestionnaireInvitesCompatibilityResult =
  DualPathRequiredMutationResult<InsertQuestionnaireInvitesResult>;

export const useInsertQuestionnaireInvitesTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.questionnaireInvite.insert.mutationOptions({
      onSuccess: async () => {
        // Apollo parity: evictField(cache, 'third_party_response') — inviting
        // writes one third_party_response per template version, which the
        // third party's questionnaires tab (and the responses register) list.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.thirdPartyResponse.pathKey(),
        });
        // COI-889: inviting writes the invite rows `useGetQuestionnaireInvites`
        // lists — no consumer renders it today, so this keeps the contract.
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.questionnaireInvite.pathKey(),
        });
      },
    }),
  });

  return {
    insertQuestionnaireInvites: async (
      input: InsertQuestionnaireInvitesInput
    ): Promise<InsertQuestionnaireInvitesResult> => {
      const result = await mutation.mutateAsync(input);

      return mapTrpcResponseToGraphQL(result);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
